import { useState } from 'react';

export type ProfileTab = 'overview' | 'skills' | 'experience' | 'preferences' | 'memberships' | 'nps';

interface Tab {
  id: ProfileTab;
  label: string;
  badge?: number;
}

const DEFAULT_TABS: Tab[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'skills', label: 'Skills' },
  { id: 'experience', label: 'Experience' },
  { id: 'preferences', label: 'Preferences' },
  { id: 'memberships', label: 'Memberships' },
  { id: 'nps', label: 'NPS' },
];

interface ProfileTabsProps {
  activeTab?: ProfileTab;
  onTabChange?: (tab: ProfileTab) => void;
  badges?: Partial<Record<ProfileTab, number>>;
}

export function ProfileTabs({ activeTab: controlledTab, onTabChange, badges }: ProfileTabsProps) {
  const [internalTab, setInternalTab] = useState<ProfileTab>('overview');
  const activeTab = controlledTab ?? internalTab;

  const handleTabClick = (tab: ProfileTab) => {
    setInternalTab(tab);
    onTabChange?.(tab);
  };

  const tabs = DEFAULT_TABS.map((t) => ({
    ...t,
    badge: badges?.[t.id],
  }));

  return (
    <div
      data-testid="profile-tabs"
      className="bp-profile-tabs"
      role="tablist"
      style={{
        display: 'flex', overflowX: 'auto', gap: 0,
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        scrollbarWidth: 'none',
      }}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => handleTabClick(tab.id)}
            style={{
              flex: '0 0 auto', padding: '10px 16px',
              background: 'none', border: 'none', borderBottom: isActive ? '2px solid var(--bp-blue-500, #2563eb)' : '2px solid transparent',
              color: isActive ? 'var(--bp-text, #f1f5f9)' : 'var(--bp-text-muted, #64748b)',
              fontSize: '0.8rem', fontWeight: isActive ? 600 : 400,
              cursor: 'pointer', whiteSpace: 'nowrap',
              transition: 'color 0.2s, border-color 0.2s',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            {tab.label}
            {tab.badge != null && tab.badge > 0 && (
              <span style={{
                fontSize: '0.65rem', padding: '1px 6px', borderRadius: 10,
                background: isActive ? 'var(--bp-blue-500, #2563eb)' : 'rgba(255,255,255,0.08)',
                color: '#fff', fontWeight: 600,
              }}>
                {tab.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
